import { useCallback, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Search,
  MapPin,
  Home,
  Laptop,
  Building,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
  Check,
} from "lucide-react";
import { isCrawlerJob, getSourceName, getSourceLabel } from "@/lib/jobUtils";
import { employmentTypeLabels, workModelLabels } from "@/components/jobs/JobCard";

interface JobFiltersProps {
  jobs: any[] | undefined;
  searchParams: URLSearchParams;
  setSearchParams: (fn: (prev: URLSearchParams) => URLSearchParams, opts?: { replace?: boolean }) => void;
}

export default function JobFilters({ jobs, searchParams, setSearchParams }: JobFiltersProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  const search = searchParams.get("q") || "";
  const filterType = searchParams.get("tipo") || null;
  const filterModel = searchParams.get("modelo") || null;
  const filterLocation = searchParams.get("local") || null;
  const filterSource = searchParams.get("fonte") || null;
  const filterSalaryMin = searchParams.get("salario_min") || null;

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const locations = useMemo(() => {
    if (!jobs) return [];
    const locationSet = new Map<string, string>();
    jobs.forEach((job) => {
      const parts = [job.location_city, job.location_state].filter(Boolean);
      if (parts.length > 0) {
        const key = parts.join(", ");
        locationSet.set(key, key);
      }
    });
    return Array.from(locationSet.values()).sort();
  }, [jobs]);

  const sourceNames = useMemo(() => {
    if (!jobs) return [];
    const sources = new Map<string, string>();
    jobs.forEach((job) => {
      if (isCrawlerJob(job)) {
        const sn = getSourceName(job);
        if (sn) sources.set(sn, getSourceLabel(sn));
      }
    });
    return Array.from(sources.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [jobs]);

  const hasActiveFilters = filterType || filterModel || filterLocation || search || filterSource || filterSalaryMin;

  const salaryOptions = [
    { label: "Qualquer", value: null },
    { label: "R$ 500+", value: "500" },
    { label: "R$ 1.000+", value: "1000" },
    { label: "R$ 1.500+", value: "1500" },
    { label: "R$ 2.000+", value: "2000" },
  ];

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="max-w-xl mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por cargo, empresa ou cidade..."
            value={search}
            onChange={(e) => updateParam("q", e.target.value || null)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Mobile: toggle filters */}
      <div className="md:hidden flex justify-center">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="gap-2"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtros
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-primary" />
          )}
          {filtersOpen ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </Button>
      </div>

      {/* Filter rows - collapsible on mobile */}
      <div
        className={`max-w-3xl mx-auto space-y-3 ${
          filtersOpen ? "block" : "hidden md:block"
        }`}
      >
        {/* Type */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">Tipo:</span>
          {Object.entries(employmentTypeLabels).map(([key, label]) => (
            <Badge
              key={key}
              variant={filterType === key ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/10 transition-colors"
              onClick={() => updateParam("tipo", filterType === key ? null : key)}
            >
              {label}
            </Badge>
          ))}
        </div>

        {/* Work model */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">Modelo:</span>
          {Object.entries(workModelLabels).map(([key, label]) => {
            const Icon = key === "remoto" ? Home : key === "hibrido" ? Laptop : Building;
            return (
              <Badge
                key={key}
                variant={filterModel === key ? "default" : "outline"}
                className="cursor-pointer hover:bg-primary/10 transition-colors"
                onClick={() => updateParam("modelo", filterModel === key ? null : key)}
              >
                <Icon className="h-3 w-3 mr-1" />
                {label}
              </Badge>
            );
          })}
        </div>

        {/* Location - dropdown/combobox */}
        {locations.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-1">
              <MapPin className="h-3.5 w-3.5 inline mr-0.5" />
              Local:
            </span>
            <Popover open={locationOpen} onOpenChange={setLocationOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                >
                  {filterLocation || "Todas as cidades"}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar cidade..." />
                  <CommandList>
                    <CommandEmpty>Nenhuma cidade encontrada</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => {
                          updateParam("local", null);
                          setLocationOpen(false);
                        }}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${
                            !filterLocation ? "opacity-100" : "opacity-0"
                          }`}
                        />
                        Todas as cidades
                      </CommandItem>
                      {locations.map((loc) => (
                        <CommandItem
                          key={loc}
                          onSelect={() => {
                            updateParam("local", loc);
                            setLocationOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              filterLocation === loc ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          {loc}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Source filter */}
        {sourceNames.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-1">Fonte:</span>
            {sourceNames.map(([key, label]) => (
              <Badge
                key={key}
                variant={filterSource === key ? "default" : "outline"}
                className="cursor-pointer hover:bg-primary/10 transition-colors"
                onClick={() => updateParam("fonte", filterSource === key ? null : key)}
              >
                {label}
              </Badge>
            ))}
          </div>
        )}

        {/* Salary filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">Bolsa:</span>
          {salaryOptions.map((opt) => (
            <Badge
              key={opt.label}
              variant={filterSalaryMin === opt.value ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/10 transition-colors"
              onClick={() => updateParam("salario_min", filterSalaryMin === opt.value ? null : opt.value)}
            >
              {opt.label}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
